import {Button, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {MeshTypeaheadItem} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshTypeaheadItem';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IListItemState} from '@mintdata/mint-kit/api/models/List';
import {ListView, ListViewConstructor} from '@mintdata/mint-kit/impl/components/list/ListView';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {inject, observer} from 'mobx-react';
import * as React from 'react';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/InputTypeahead.less');
const cx = classnames.bind(styles);

interface IInputTypeaheadProps
{
    ctx?:IWSPMeshContext;
}

@inject('ctx')
@observer
export class InputTypeahead extends React.Component<IInputTypeaheadProps, any>
{
    private readonly _id:GUID = IdUtil.newGUID();

    @bind
    private _onItemClick(typeaheadItem:MeshTypeaheadItem<any>):void
    {
        const {ctx} = this.props;
        controllers.ui.spreadsheets.meshInput.applyTypeaheadItem(ctx, typeaheadItem);
    }

    @bind
    private _onClickFunctionDoc(e:React.MouseEvent<HTMLElement>, functionName:string, isCloudFunction:boolean = false):void
    {
        e.stopPropagation();
        e.preventDefault();

        const {lx} = controllers.ui;

        lx.showLxFrame();

        lx.postMessage({
            source: 'designer',
            event_type: 'show_function_docs',
            has_function_name: !!functionName,
            function_name: functionName ? functionName.toLowerCase() : null,
            is_cloud_flow: isCloudFunction,
        });
    }

    @bind
    private _typeaheadItemRenderer(data:MeshTypeaheadItem<any>, state:IListItemState):JSX.Element
    {
        const {title, tip, pillText, datum} = data;
        const {isSelected} = state;

        const isCloudFunction = datum && datum.namespace && datum.namespace.toLowerCase() === 'cloud'
            || pillText && pillText.toLowerCase() === 'cloud';

        return (
            <div className={cx('itemRoot', {isSelected})}>

                <div className={cx('wrapperLeft')}>
                    <div className={styles.itemValueWrapper}>
                        <div className={styles.itemValue}>
                            {title}
                        </div>
                    </div>
                    {
                        (tip && isSelected) &&
                        <div className={styles.itemTip}>
                            {tip}
                        </div>
                    }
                </div>

                <div
                    className={cx('iconDoc')}
                    onClick={(e:React.MouseEvent<HTMLElement>) => this._onClickFunctionDoc(e, title, isCloudFunction)}
                >
                    <WSPIcon
                        name={WSPIcons.visualDesigner.spreadsheet.Help}
                        size={WSPIconSize.medium20}
                    />
                </div>

            </div>
        );
    }

    private _popoverSource:HTMLElement = null;

    @bind
    private _onHandlePopoverSourceRef(elementRef:HTMLElement)
    {
        const {sheetId} = this.props.ctx;

        DomUtils.eachParentElement(elementRef, parentElement =>
        {
            if( !parentElement )
                return false;

            if( parentElement.id === sheetId )
            {
                this._popoverSource = parentElement;
                return false;
            }
        });
    }

    @bind
    private _onClickCloseBtn():void
    {
        const {meshInput} = controllers.ui.spreadsheets;

        meshInput.hideTypeahead();
        meshInput.setDocVisibility(false);
    }

    public render():JSX.Element
    {
        const inputState = controllers.ui.spreadsheets.main.getEditorState().input;
        const {isTypeaheadOpen, typeaheadList} = inputState;
        const TypedList:ListViewConstructor<MeshTypeaheadItem<any>> = ListView as any;

        const isOne = typeaheadList && typeaheadList.getSize() === 1;
        let funcIsCloud = false;

        let about = 'functions';

        if( isOne )
        {
            const item1 = typeaheadList.getItems()[0].getData() as MeshTypeaheadItem<any>;
            about = item1.title;

            if( item1.datum && item1.datum.namespace && item1.datum.namespace.toLowerCase() === 'cloud'
                || item1.pillText && item1.pillText.toLowerCase() === 'cloud' )
            {
                about = 'cloud functions';
                funcIsCloud = true;
            }
        }

        return <div ref={this._onHandlePopoverSourceRef}>
            {
                isTypeaheadOpen && typeaheadList &&
                <Popover id={this._id}
                         popupSource={this._popoverSource}
                         sourceAnchor={SourceAnchor.LeftBottom}
                         popupAnchor={PopupAnchor.LeftTop}
                         ignoreEsc={true}
                         isModal={false}
                         hasModalBackground={true}
                         className={cx('popover')}
                >
                    <TypedList dataSource={typeaheadList}
                               itemRenderer={this._typeaheadItemRenderer}
                               onItemClick={this._onItemClick}
                               className={cx('list')}
                               theme={styles}
                               selectOnItemHover={true}
                    />
                    <div className={cx('popoverFooter')}>
                        <div
                            className={cx('footerLink')}
                            onClick={(e:React.MouseEvent<HTMLElement>) =>
                            {
                                this._onClickFunctionDoc(e, isOne ? funcIsCloud ? '' : about : '', funcIsCloud);
                            }}
                        >
                            Learn more about {about}
                        </div>
                        <Button
                            className={cx('closeBtn')}
                            style={ButtonStyle.SimpleSecondary}
                            icon={MDIcons.common.Close}
                            iconSize={MDIconSize.medium16}
                            onClick={this._onClickCloseBtn}
                        />
                    </div>
                </Popover>
            }
        </div>;
    }
}
